if (!customElements.get('aureme-size-dropdown')) {
  const AUREME_SIZE_FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');
  const AUREME_SIZE_MENU_GUTTER = 16;
  const AUREME_SIZE_MENU_GAP = 4;
  const AUREME_SIZE_SELECTORS = {
    label: '[data-aureme-size-label]',
    menu: '[data-aureme-size-menu]',
    nativeSelect: '[data-aureme-size-native-select]',
    nativeSelectWrapper: '.aureme-size-dropdown__native',
    option: '[data-aureme-size-option]',
    trigger: '[data-aureme-size-trigger]',
  };

  const getInteractiveOptions = (optionElements) =>
    optionElements.filter((button) => !button.disabled);

  const measureMenuSizing = ({ triggerRect, viewportWidth }) => {
    const maxWidth = Math.max(0, viewportWidth - AUREME_SIZE_MENU_GUTTER * 2);
    const minWidth = Math.min(Math.ceil(triggerRect.width), maxWidth);

    return {
      maxWidth,
      minWidth,
    };
  };

  const measureMenuPlacement = ({ menuRect, triggerRect, viewportHeight, viewportWidth }) => {
    /* メニューを viewport 基準に再配置し、行の overflow 制御に影響されないようにする */
    const leftBoundary = AUREME_SIZE_MENU_GUTTER;
    const topBoundary = AUREME_SIZE_MENU_GUTTER;
    const rightBoundary = Math.max(
      leftBoundary,
      viewportWidth - menuRect.width - AUREME_SIZE_MENU_GUTTER,
    );
    const bottomBoundary = viewportHeight - AUREME_SIZE_MENU_GUTTER;
    const nextLeft = Math.min(Math.max(triggerRect.left, leftBoundary), rightBoundary);
    const bottomTop = triggerRect.bottom + AUREME_SIZE_MENU_GAP;
    const topCandidate = triggerRect.top - menuRect.height - AUREME_SIZE_MENU_GAP;
    const nextTop =
      bottomTop + menuRect.height <= bottomBoundary
        ? bottomTop
        : Math.max(topBoundary, topCandidate);

    return {
      left: Math.round(nextLeft),
      top: Math.round(nextTop),
    };
  };

  const isFocusableCandidateAfterTrigger = (element, menuElement) => {
    if (menuElement?.contains(element)) return false;
    if (element.closest(AUREME_SIZE_SELECTORS.nativeSelectWrapper)) return false;
    return element.getClientRects().length > 0;
  };

  const findNextFocusableAfterTrigger = ({ focusableElements, menuElement, triggerElement }) => {
    if (!triggerElement) return null;

    const triggerIndex = focusableElements.indexOf(triggerElement);

    if (triggerIndex === -1) return null;

    for (const element of focusableElements.slice(triggerIndex + 1)) {
      if (!isFocusableCandidateAfterTrigger(element, menuElement)) continue;

      return element;
    }

    return null;
  };

  /*
   * native select を唯一の状態源にし、カスタムメニューは表示時だけ body へ退避する。
   * 商品フォーム行の overflow に切られない代わりに、focus 移動と位置復元をここで一括管理する。
   */
  customElements.define(
    'aureme-size-dropdown',
    class AuremeSizeDropdown extends HTMLElement {
      handleTriggerClick = (event) => {
        event.preventDefault();
        this.toggleMenu();
      };

      handleSelectChange = () => {
        this.syncFromSelect();
      };

      handleDocumentClick = (event) => {
        if (this.contains(event.target) || this.menuElement?.contains(event.target)) return;
        this.closeMenu();
      };

      handleViewportChange = () => {
        if (this.isMenuOpen()) {
          const shouldRestoreFocus = this.menuElement.contains(document.activeElement);
          this.closeMenu({ focusTrigger: shouldRestoreFocus });
        }
      };

      handleKeydown = (event) => {
        if (this.handleTabKeydown(event)) return;

        if (event.key !== 'Escape') return;

        this.closeMenu({ focusTrigger: true });
      };

      handleOptionClick = (event) => {
        const button = event.currentTarget;
        const nextValue = button.dataset.value;
        if (!this.selectElement || !nextValue) return;

        if (this.selectElement.value !== nextValue) {
          /* 値の反映は必ず native select の change を経由し、variant 更新系の既存リスナーへ伝搬させる */
          this.selectElement.value = nextValue;
          this.selectElement.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          this.syncFromSelect();
        }

        this.closeMenu({ focusTrigger: true });
      };

      handleOptionFocus = (event) => {
        event.currentTarget.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      };

      connectedCallback() {
        this.cacheDomReferences();

        if (!this.hasRequiredElements()) return;

        this.addComponentListeners();

        this.closeMenu();
        this.syncFromSelect();
      }

      disconnectedCallback() {
        this.removeComponentListeners();
        this.deactivateMenuLayer();
        this._selectEl = undefined;
        this._triggerEl = undefined;
        this._labelEl = undefined;
        this._menuEl = undefined;
        this._optionEls = undefined;
      }

      /* DOM参照をキャッシュして繰り返しクエリを回避 */
      cacheDomReferences() {
        this._selectEl = this.querySelector(AUREME_SIZE_SELECTORS.nativeSelect);
        this._triggerEl = this.querySelector(AUREME_SIZE_SELECTORS.trigger);
        this._labelEl = this.querySelector(AUREME_SIZE_SELECTORS.label);
        this._menuEl = this.querySelector(AUREME_SIZE_SELECTORS.menu);
        this._optionEls = Array.from(this.querySelectorAll(AUREME_SIZE_SELECTORS.option));
      }

      hasRequiredElements() {
        return Boolean(this._selectEl && this._triggerEl && this._menuEl);
      }

      addComponentListeners() {
        this._triggerEl.addEventListener('click', this.handleTriggerClick);
        this._selectEl.addEventListener('change', this.handleSelectChange);
        this.addOptionListeners();
        this.addEventListener('keydown', this.handleKeydown);
        this._menuEl.addEventListener('keydown', this.handleKeydown);
        document.addEventListener('click', this.handleDocumentClick);
      }

      removeComponentListeners() {
        this._triggerEl?.removeEventListener('click', this.handleTriggerClick);
        this._selectEl?.removeEventListener('change', this.handleSelectChange);
        this.removeOptionListeners();
        this.removeEventListener('keydown', this.handleKeydown);
        this._menuEl?.removeEventListener('keydown', this.handleKeydown);
        document.removeEventListener('click', this.handleDocumentClick);
      }

      addOptionListeners() {
        for (const button of this.optionElements) {
          button.addEventListener('click', this.handleOptionClick);
          button.addEventListener('focus', this.handleOptionFocus);
        }
      }

      removeOptionListeners() {
        for (const button of this.optionElements) {
          button.removeEventListener('click', this.handleOptionClick);
          button.removeEventListener('focus', this.handleOptionFocus);
        }
      }

      get selectElement() {
        return this._selectEl || null;
      }

      get triggerElement() {
        return this._triggerEl || null;
      }

      get labelElement() {
        return this._labelEl || null;
      }

      get menuElement() {
        return this._menuEl || null;
      }

      get optionElements() {
        return this._optionEls || [];
      }

      get sizeInputRow() {
        return this.closest('.product-form__input--aureme-size');
      }

      isMenuOpen() {
        return this.menuElement?.hidden === false;
      }

      handleTabKeydown(event) {
        if (event.key !== 'Tab' || !this.menuElement?.contains(event.target)) return false;

        /*
         * メニューは body 直下へ退避しているため、先頭/末尾の Tab 移動だけは
         * 元のトリガー位置を基準にした focus 順へ手動で戻す。
         */
        const interactiveOptions = getInteractiveOptions(this.optionElements);
        const firstOption = interactiveOptions[0];
        const lastOption = interactiveOptions[interactiveOptions.length - 1];

        if (event.shiftKey && event.target === firstOption) {
          event.preventDefault();
          this.closeMenu({ focusTrigger: true });
          return true;
        }

        if (!event.shiftKey && event.target === lastOption) {
          event.preventDefault();
          const focusableElements = Array.from(
            document.querySelectorAll(AUREME_SIZE_FOCUSABLE_SELECTOR),
          );
          const nextFocusable = findNextFocusableAfterTrigger({
            focusableElements,
            menuElement: this.menuElement,
            triggerElement: this.triggerElement,
          });
          this.closeMenu();
          nextFocusable?.focus();
          return true;
        }

        return false;
      }

      get nativeSelectWrapper() {
        return this.querySelector(AUREME_SIZE_SELECTORS.nativeSelectWrapper);
      }

      mountMenuLayer() {
        if (!this.menuElement || this.menuElement.parentElement === document.body) return;

        document.body.appendChild(this.menuElement);
      }

      restoreMenuLayer() {
        if (!this.menuElement || this.menuElement.parentElement === this) return;

        // body 退避前の DOM 順に戻し、native select fallback との読み上げ順を維持する。
        if (this.nativeSelectWrapper) {
          this.insertBefore(this.menuElement, this.nativeSelectWrapper);
        } else {
          this.appendChild(this.menuElement);
        }
      }

      showMenuLayer() {
        this.mountMenuLayer();
        this.menuElement.hidden = false;
      }

      hideMenuLayer() {
        this.menuElement.hidden = true;
      }

      activateMenuLayer() {
        this.applyMenuLayerPosition();
        this.focusSelectedMenuLayerOption();
        this.addMenuLayerViewportChangeListeners();
      }

      deactivateMenuLayer() {
        this.removeMenuLayerViewportChangeListeners();
        this.resetMenuLayerPosition();
        this.restoreMenuLayer();
      }

      resetMenuLayerPosition() {
        if (!this.menuElement) return;

        this.menuElement.style.removeProperty('top');
        this.menuElement.style.removeProperty('left');
        this.menuElement.style.removeProperty('min-width');
        this.menuElement.style.removeProperty('max-width');
      }

      focusSelectedMenuLayerOption() {
        const selectedButton =
          this.optionElements.find((button) => button.classList.contains('is-selected')) ||
          this.optionElements[0];

        if (!selectedButton) return;

        selectedButton.focus({ preventScroll: true });
        selectedButton.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }

      applyMenuLayerPosition() {
        if (!this.menuElement || !this.triggerElement) return;

        const triggerRect = this.triggerElement.getBoundingClientRect();
        const sizing = measureMenuSizing({
          triggerRect,
          viewportWidth: window.innerWidth,
        });

        this.menuElement.style.maxWidth = `${sizing.maxWidth}px`;
        this.menuElement.style.minWidth = `${sizing.minWidth}px`;

        const placement = measureMenuPlacement({
          menuRect: this.menuElement.getBoundingClientRect(),
          triggerRect,
          viewportHeight: window.innerHeight,
          viewportWidth: window.innerWidth,
        });

        this.menuElement.style.left = `${placement.left}px`;
        this.menuElement.style.top = `${placement.top}px`;
      }

      /* body へ退避したサイズメニューは、viewport や横スクロールが動いた時点で閉じて位置ずれを避ける */
      addMenuLayerViewportChangeListeners() {
        window.addEventListener('resize', this.handleViewportChange);
        window.addEventListener('scroll', this.handleViewportChange);
        this.sizeInputRow?.addEventListener('scroll', this.handleViewportChange);
      }

      removeMenuLayerViewportChangeListeners() {
        window.removeEventListener('resize', this.handleViewportChange);
        window.removeEventListener('scroll', this.handleViewportChange);
        this.sizeInputRow?.removeEventListener('scroll', this.handleViewportChange);
      }

      toggleMenu() {
        if (this.isMenuOpen()) {
          this.closeMenu();
        } else {
          this.openMenu();
        }
      }

      openMenu() {
        if (!this.menuElement || !this.triggerElement) return;

        this.classList.add('is-open');
        this.showMenuLayer();
        this.triggerElement.setAttribute('aria-expanded', 'true');
        this.activateMenuLayer();
      }

      closeMenu({ focusTrigger = false } = {}) {
        if (!this.menuElement || !this.triggerElement) return;

        this.classList.remove('is-open');
        this.hideMenuLayer();
        this.triggerElement.setAttribute('aria-expanded', 'false');
        this.deactivateMenuLayer();

        if (focusTrigger) {
          this.triggerElement.focus();
        }
      }

      syncFromSelect() {
        if (!this.selectElement) return;

        const selectedOption = this.selectElement.selectedOptions[0];
        const selectedLabel =
          selectedOption?.dataset.optionLabel || selectedOption?.textContent?.trim() || '';
        const selectedValue = this.selectElement.value;

        if (this.labelElement) {
          this.labelElement.textContent = selectedLabel;
        }

        for (const button of this.optionElements) {
          const isSelected = button.dataset.value === selectedValue;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        }
      }
    },
  );
}
